import { isBookEffectivelyFinished, type BookSummary } from '../models';

// Books filter & sort options: Flutter's BookFilterOptions
// (lib/domain/entities/book_filter_options.dart) plus facet filters the
// web build adds (genres, tags, authors, narrators, series) and two extra
// progress states.

export type BookSortBy = 'dateAdded' | 'title' | 'duration' | 'author' | 'narrator' | 'dateReleased' | 'lastPlayed' | 'fileSize';
export type SortOrder = 'ascending' | 'descending';
/** `all` / `finished` / `unfinished` are Flutter's; `inProgress` / `notStarted` split `unfinished`. */
export type BookStatusFilter = 'all' | 'finished' | 'unfinished' | 'inProgress' | 'notStarted';
export type AvailabilityFilter = 'showAll' | 'downloadedOnly';

export interface BookSort {
  sortBy: BookSortBy;
  sortOrder: SortOrder;
}

export interface BookFilters {
  status: BookStatusFilter;
  availability: AvailabilityFilter;
  /** Any of these genres (case-insensitive). */
  genres: string[];
  tags: string[];
  /** Author ids or names. */
  authors: string[];
  narrators: string[];
  /** Series ids or names. */
  series: string[];
}

export type BookFilterOptions = BookSort & BookFilters;

/** Sort chips in Flutter's order with their labels. */
export const BOOK_SORT_OPTIONS: ReadonlyArray<{ value: BookSortBy; label: string }> = [
  { value: 'dateAdded', label: 'Date Added' },
  { value: 'title', label: 'Title' },
  { value: 'duration', label: 'Book Length' },
  { value: 'author', label: 'Author' },
  { value: 'narrator', label: 'Narrator' },
  { value: 'dateReleased', label: 'Date Released' },
  { value: 'lastPlayed', label: 'Last Played' },
  { value: 'fileSize', label: 'File Size' },
];

export const SORT_ORDER_OPTIONS: ReadonlyArray<{ value: SortOrder; label: string }> = [
  { value: 'ascending', label: 'Ascending' },
  { value: 'descending', label: 'Descending' },
];

export const STATUS_OPTIONS: ReadonlyArray<{ value: BookStatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'finished', label: 'Finished' },
  { value: 'unfinished', label: 'Unfinished' },
];

export const AVAILABILITY_OPTIONS: ReadonlyArray<{ value: AvailabilityFilter; label: string }> = [
  { value: 'showAll', label: 'Show All' },
  { value: 'downloadedOnly', label: 'Downloaded Only' },
];

export const DEFAULT_BOOK_SORT: BookSort = { sortBy: 'dateAdded', sortOrder: 'descending' };

export const DEFAULT_BOOK_FILTERS: BookFilters = {
  status: 'all',
  availability: 'showAll',
  genres: [],
  tags: [],
  authors: [],
  narrators: [],
  series: [],
};

export const DEFAULT_BOOK_FILTER_OPTIONS: BookFilterOptions = { ...DEFAULT_BOOK_SORT, ...DEFAULT_BOOK_FILTERS };

/** Fills defaults for missing fields. */
export function normalizeFilterOptions(partial: Partial<BookFilterOptions> = {}): BookFilterOptions {
  return {
    sortBy: partial.sortBy ?? DEFAULT_BOOK_SORT.sortBy,
    sortOrder: partial.sortOrder ?? DEFAULT_BOOK_SORT.sortOrder,
    status: partial.status ?? 'all',
    availability: partial.availability ?? 'showAll',
    genres: partial.genres ?? [],
    tags: partial.tags ?? [],
    authors: partial.authors ?? [],
    narrators: partial.narrators ?? [],
    series: partial.series ?? [],
  };
}

const sameList = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((x, i) => x === b[i]);

export function filterOptionsEqual(a: BookFilterOptions, b: BookFilterOptions): boolean {
  return (
    a.sortBy === b.sortBy &&
    a.sortOrder === b.sortOrder &&
    a.status === b.status &&
    a.availability === b.availability &&
    sameList(a.genres, b.genres) &&
    sameList(a.tags, b.tags) &&
    sameList(a.authors, b.authors) &&
    sameList(a.narrators, b.narrators) &&
    sameList(a.series, b.series)
  );
}

/** Flutter `hasActiveFilters`: any field differs from its default, sort included. */
export function hasActiveFilters(options: Partial<BookFilterOptions>): boolean {
  return !filterOptionsEqual(normalizeFilterOptions(options), DEFAULT_BOOK_FILTER_OPTIONS);
}

const lower = (s: string): string => s.trim().toLowerCase();

function anyMatch(selected: readonly string[], values: readonly string[]): boolean {
  if (selected.length === 0) return true;
  const set = new Set(values.map(lower));
  return selected.some((s) => set.has(lower(s)));
}

/** Status test shared by every list (0.98 finished threshold). */
export function matchesStatus(book: BookSummary, status: BookStatusFilter): boolean {
  if (status === 'all') return true;
  const finished = isBookEffectivelyFinished(book);
  switch (status) {
    case 'finished':
      return finished;
    case 'unfinished':
      return !finished;
    case 'inProgress':
      return !finished && book.currentTime > 0;
    case 'notStarted':
      return !finished && book.currentTime <= 0;
  }
}

export function matchesFilters(book: BookSummary, filters: BookFilters): boolean {
  if (!matchesStatus(book, filters.status)) return false;
  if (filters.availability === 'downloadedOnly' && !book.isDownloaded) return false;
  if (!anyMatch(filters.genres, book.genres)) return false;
  if (!anyMatch(filters.tags, book.tags)) return false;
  if (!anyMatch(filters.authors, [...book.authors.map((a) => a.id).filter(Boolean), ...book.authors.map((a) => a.name), book.authorName])) return false;
  if (!anyMatch(filters.narrators, book.narrators)) return false;
  if (!anyMatch(filters.series, [...book.series.map((s) => s.id).filter(Boolean), ...book.series.map((s) => s.name)])) return false;
  return true;
}

export function filterBooks(books: readonly BookSummary[], filters: BookFilters): BookSummary[] {
  return books.filter((b) => matchesFilters(b, filters));
}
